import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["src", "alt"]
const _hoisted_2 = ["src", "alt"]
const _hoisted_3 = {
  key: 2,
  class: "absolute left-2 top-2 z-10 rounded-sm bg-label px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white"
}
const _hoisted_4 = {
  key: 3,
  class: "absolute inset-x-0 top-1/2 z-10 -translate-y-1/2 text-center"
}
const _hoisted_5 = { class: "inline-block bg-[rgba(84,144,164,0.5)] px-9 py-2 text-xs font-bold uppercase text-white backdrop-blur-sm" }
const _hoisted_6 = { class: "product-card__bottom bg-transparent px-1 py-2" }
const _hoisted_7 = { class: "m-0 truncate text-[11px] uppercase tracking-wider text-muted-foreground" }
const _hoisted_8 = { class: "m-0 mt-0.5 line-clamp-2 font-mulish text-[14px] font-normal leading-tight text-foreground" }
const _hoisted_9 = {
  key: 0,
  class: "mt-1.5 flex items-center gap-1.5"
}
const _hoisted_10 = {
  key: 1,
  class: "m-0 mt-1 text-[11px] text-muted-foreground"
}

import { ref, computed, onMounted } from 'vue';

import useWebsiteTexts from '@/composables/useWebsiteTexts';
import useContext from '@/composables/useContext';
import useGiftCard from '@/composables/useGiftCard';
import { ImageSizeHelper } from '@/helpers/image-size';
import { deviceWidths } from '../constants';

import FavoriteButton from '@/components/FavoriteButton.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductCard',
  props: {
  product: { type: Object, required: true },
  categoryId: { type: String, default: null },
  productSections: { type: Array, default: () => [] },
  tag: { type: Object, default: null },
  isFavoriteProduct: { type: Boolean, default: false },
  showFavoriteProducts: { type: Boolean, default: false }
},
  setup(__props) {

const props = __props;

const { toLitiumMediaUrl } = useContext();
const { websiteText } = useWebsiteTexts();
const { toggleFavoriteProduct } = useGiftCard();

const hover = ref(false);

function onHoverStart(e: PointerEvent) {
  if (e.pointerType === 'mouse') hover.value = true;
}

function onHoverEnd(e: PointerEvent) {
  if (e.pointerType === 'mouse') hover.value = false;
}

const baseImgWidth = typeof window !== 'undefined' ? (window.innerWidth < deviceWidths.phoneMaxWidth ? window.innerWidth : window.innerWidth / 2) : 600;
const cardImgWidth = props.product?.highlighted ? baseImgWidth : baseImgWidth / 2;
const resolvedWidth = ImageSizeHelper.getImageSize(cardImgWidth);

const productUrl = computed(() => {
  if (!props.product?.url) return '';
  if (props.tag?.key && props.tag.value) {
    const sectionQueryParamValue = `${props.tag.key || ''}::${props.tag.value || ''}`;
    return `${props.product.url}?section=${encodeURIComponent(sectionQueryParamValue)}`;
  }
  return props.product.url;
});

function imageUrl(id: string | null | undefined, version?: string | null) {
  if (!id) return '';
  let url = toLitiumMediaUrl(id, { maxWidth: resolvedWidth });
  if (version) url = `${url}&v=${version}`;
  return url;
}

const primaryImageUrl = computed(() =>
  imageUrl(props.product?.primaryImage || props.product?.imageId || props.product?.imageIds?.[0], props.product?.primaryImageVersion)
);

const hoverImageUrl = computed(() => {
  const id = props.product?.secondaryImage;
  if (!id) return '';
  return imageUrl(id, props.product?.secondaryImageVersion);
});

const productName = computed(() => props.product?.productName || props.product?.name || '');
const brandName = computed(() => props.product?.brandName || '');
const comingSoon = computed(() => props.product?.comingSoon ?? false);
const lowImpactFlag = computed(() => props.product?.showLowImpactFlag ?? false);

const colorSwatches = computed<string[]>(() => {
  const raw = props.product?.colors;
  if (!Array.isArray(raw)) return [];
  return raw.filter((c: any) => typeof c === 'string' && c.length > 0);
});
const hasMultipleColors = computed(() => colorSwatches.value.length > 1);

const hasMultipleSizes = computed(() => Boolean(props.product?.hasMultipleSizes));

function onToggleFavorite() {
  // FavoriteButton already does @click.stop.prevent; pass a stub event so
  // toggleFavoriteProduct's preventDefault/stopPropagation calls don't throw.
  toggleFavoriteProduct({ preventDefault() {}, stopPropagation() {} } as Event, props.product);
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(_component_router_link, {
    to: productUrl.value,
    class: "product-card group grid no-underline",
    style: {"grid-template-rows":"subgrid","grid-row":"span 2","row-gap":"0"}
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        class: "product-card__top relative aspect-[3/4] overflow-hidden rounded-sm bg-white",
        onPointerenter: onHoverStart,
        onPointerleave: onHoverEnd
      }, [
        (primaryImageUrl.value)
          ? (_openBlock(), _createElementBlock("img", {
              key: 0,
              src: primaryImageUrl.value,
              alt: productName.value,
              loading: "lazy",
              class: _normalizeClass(['absolute inset-0 h-full w-full object-contain p-[5px] transition-opacity duration-300', hoverImageUrl.value && hover.value ? 'opacity-0' : 'opacity-100'])
            }, null, 10, _hoisted_1))
          : _createCommentVNode("", true),
        (hoverImageUrl.value)
          ? (_openBlock(), _createElementBlock("img", {
              key: 1,
              src: hoverImageUrl.value,
              alt: productName.value,
              loading: "lazy",
              class: _normalizeClass(['absolute inset-0 h-full w-full object-cover transition-opacity duration-300', hover.value ? 'opacity-100' : 'opacity-0'])
            }, null, 10, _hoisted_2))
          : _createCommentVNode("", true),
        (lowImpactFlag.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_unref(websiteText)('common_lowimpact').value), 1))
          : _createCommentVNode("", true),
        (comingSoon.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(websiteText)('common_comingsoon').value), 1)
            ]))
          : _createCommentVNode("", true),
        (__props.showFavoriteProducts)
          ? (_openBlock(), _createBlock(FavoriteButton, {
              key: 4,
              "is-favorite": __props.isFavoriteProduct,
              size: "sm",
              class: "absolute right-2 top-2 z-10 p-1",
              "aria-label": _unref(websiteText)('productdetailspage_favorite').value,
              onToggle: onToggleFavorite
            }, null, 8, ["is-favorite", "aria-label"]))
          : _createCommentVNode("", true)
      ], 32),
      _createElementVNode("div", _hoisted_6, [
        _createElementVNode("p", _hoisted_7, _toDisplayString(brandName.value), 1),
        _createElementVNode("h3", _hoisted_8, _toDisplayString(productName.value), 1),
        (hasMultipleColors.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(colorSwatches.value, (color, i) => {
                return (_openBlock(), _createElementBlock("span", {
                  key: i,
                  class: "inline-block h-[8.75px] w-[8.75px] rounded-full border",
                  style: _normalizeStyle({ backgroundColor: color }),
                  "aria-hidden": "true"
                }, null, 4))
              }), 128))
            ]))
          : _createCommentVNode("", true),
        (hasMultipleSizes.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(_unref(websiteText)('plp_multiplesizes').value), 1))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["to"]))
}
}

})